import React, { useState, useEffect   } from "react";
import { MuiTelInput } from 'mui-tel-input';
import { db } from "../../firebase-config";
import { useParams, Link } from "react-router-dom";
import { ref, onValue, set } from "firebase/database";
import { ToastContainer, toast } from "react-toastify";

const initialState = {
  Email: "",
  FullName: "",
  Password: "",
  Role: "",
  CostingType: "",
  Contact: "",
};

function EditAdmin() {
  // Currency state
  const [currency, setCurrency] = useState('INR'); // Default to INR
  const [state, setState] = useState(initialState);
  const [data, setData] = useState({});
  const [costing, setCosting] = useState({});
  // State for tracking which AI models are enabled
  const [aiEnabled, setAiEnabled] = useState({});
  // State for tracking which AI models have AUTO RUN enabled
  const [autoRunEnabled, setAutoRunEnabled] = useState({});
  const [modelsToRemove, setModelsToRemove] = useState([]);
  const { Email, FullName, Password, Role, CostingType } = state;
  const [contact, setContact] = useState("");
  const { id } = useParams();


  // Load admin data from DB
  useEffect(() => {
    const usersRef = ref(db, `superadmin/admins`);
    onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        setData({ ...snapshot.val() });
      } else {
        setData({});
      }
    });
    return () => setData({});
  }, [id]);

  // Set state and costing from the selected admin
  useEffect(() => {
    if (id && data[id]) {
      const adminData = data[id];
      setState({ ...initialState, ...adminData });
      setContact(adminData.Contact || "");
      const adminRef = ref(db, `superadmin/admins/${id}`);
      onValue(adminRef, (snapshot) => {
        if (snapshot.exists()) {
          const snapVal = snapshot.val();
          if (snapVal.costing) {
            setCosting(snapVal.costing);
          } else {
            setCosting({});
          }
          // Set currency if present
          if (snapVal.Currency) {
            setCurrency(snapVal.Currency);
          } else {
            setCurrency(''); // No currency, force user to choose
          }
        } else {
          setCosting({});
          setCurrency('INR');
        }
      });
    } else {
      setState({ ...initialState });
      setCurrency('INR');
    }
    return () => setState({ ...initialState });
  }, [id, data]);

  // Load enabled AI models flags from DB (if available)
  useEffect(() => {
    if (id) {
      const enabledRef = ref(db, `superadmin/admins/${id}/AI_Model_Enabled`);
      onValue(enabledRef, (snapshot) => {
        if (snapshot.exists()) {
          setAiEnabled(snapshot.val());
        } else {
          // If the node doesn't exist, leave aiEnabled empty for now.
          setAiEnabled({});
        }
      });
    }
  }, [id]);

  // Load AUTO RUN enabled AI models flags from DB (if available)
  useEffect(() => {
    if (id) {
      const autoRunRef = ref(db, `superadmin/admins/${id}/AI_Model_AutoRun`);
      onValue(autoRunRef, (snapshot) => {
        if (snapshot.exists()) {
          setAutoRunEnabled(snapshot.val());
        } else {
          setAutoRunEnabled({});
        }
      });
    }
  }, [id]);

  // Always derive AI models enabled flags from costing.
  // If a model's pricing is set (non-empty), mark that model as enabled.
  useEffect(() => {
    // Only derive if an admin exists, costing has been loaded, and no manual override exists yet.
    if (
      id &&
      Object.keys(aiEnabled).length === 0 &&
      Object.keys(costing).length > 0
    ) {
      const modelKeys = [
        "Bone_Fracture_Model",
        "ThreeD_AI_Model",
        "Brain_Tumour_Model",
        "ICH_Model",
        "Contouring_Model",
        // Also check for new model names from AddAdmin
        "CXR-TB", 
        "CXR-TB2",
        "CXR-Multiclass",
        "XRay-Fracture",
        "3D-Abdomen", 
        "3D-HaN",
        "Contour-Abdomen",
        "Contour-HaN",
        "Tumor-Abdomen",
        "Liver-Segmentation"
      ];
      const derived = {};
      modelKeys.forEach((key) => {
        if (costing[key] && costing[key].toString().trim() !== "") {
          derived[key] = true;
        }
      });
      if (Object.keys(derived).length > 0) {
        setAiEnabled(derived);
      }
    }
  }, [costing, id, aiEnabled]);
  

  // Track which models need to be removed from database
  

  // Helper function to update AI model flags manually in state
  const updateAiEnabled = (modelName, checked) => {
    // Update the enabled flag
    setAiEnabled((prev) => {
      const newEnabled = { ...prev };
      if (!checked) {
        delete newEnabled[modelName]; // Completely remove the key
        
        // Also disable AUTO RUN when AI is disabled
        setAutoRunEnabled((prevAutoRun) => {
          const newAutoRun = { ...prevAutoRun };
          delete newAutoRun[modelName];
          return newAutoRun;
        });
        
        // Add to the list of models to remove from database on save
        setModelsToRemove(prevModels => {
          if (!prevModels.includes(modelName)) {
            return [...prevModels, modelName];
          }
          return prevModels;
        });
      } else {
        newEnabled[modelName] = true;
        
        // Remove from the list of models to remove if it was previously added
        setModelsToRemove(prevModels => 
          prevModels.filter(model => model !== modelName)
        );
      }
      return newEnabled;
    });
    
    // For checked models, ensure we have a price field
    // We keep price data in memory when unchecking to preserve values in case user changes mind before saving
    if (checked && !costing[modelName]) {
      // If checked but no price exists yet, initialize with empty price
      // This ensures the price field appears for the user to enter a value
      setCosting(prevCosting => ({
        ...prevCosting,
        [modelName]: ""
      }));
    }
  };

  // Helper function to update AUTO RUN flags
  const updateAutoRunEnabled = (modelName, checked) => {
    setAutoRunEnabled((prev) => {
      const newAutoRun = { ...prev };
      if (!checked) {
        delete newAutoRun[modelName];
      } else {
        newAutoRun[modelName] = true;
      }
      return newAutoRun;
    });
  };


  // Handler for form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState({ ...state, [name]: value });
  };

  // Handler for contact input
  const handleContactChange = (value) => {
    const sanitized = value.replace(/\s+/g, '');
    setContact(sanitized);
    setState((prev) => ({ ...prev, Contact: sanitized }));
  };

  const handleCostingChange = (e) => {
    const { name, value, dataset } = e.target;
    if (dataset && dataset.type) {
      setCosting({ ...costing, CPU: { ...costing.CPU, [name]: value } });
    } else {
      // Check if this is an AI model input (for all costing types)
      const aiModelNames = [
        "Bone_Fracture_Model",
        "ThreeD_AI_Model",
        "Brain_Tumour_Model",
        "ICH_Model",
        "Contouring_Model",
        "CXR-TB", 
        "CXR-TB2",
        "CXR-Multiclass",
        "XRay-Fracture",
        "3D-Abdomen", 
        "3D-HaN",
        "Contour-Abdomen",
        "Contour-HaN",
        "Tumor-Abdomen",
        "Liver-Segmentation"
      ];
      
      if (aiModelNames.includes(name)) {
        // Update costing directly for AI models regardless of costing type
        setCosting({ ...costing, [name]: value });
        
        // Also ensure the model is marked as enabled if it has a price
        if (value && value.trim() !== "") {
          setAiEnabled(prev => ({ ...prev, [name]: true }));
        }
      } else {
        // Handle regular costing fields
        setCosting({ ...costing, [name]: value });
      }
    }
  };

  // Render costing inputs based on the CostingType
  const renderCostingInputs = () => {
    // Initialize with an empty array to store all input components
    const inputs = [];
    
    // Render inputs based on costing type
    if (state.CostingType === "Prepaid") {
      // No additional fields for Prepaid
    } else {
      // For other costing types (CPU, CPV, CPU+CPV)
      if (costing.CPU) {
        inputs.push(
          ...Object.entries(costing.CPU).map(([key, value]) => (
            <div key={key}>
              <strong className="strongaddadmin">{key} :</strong>
              <input
                className="inputaddadmin inputPN"
                type="text"
                id={key}
                name={key}
                data-type="CPU"
                placeholder={`Enter ${key} Pricing`}
                value={value || ""}
                onChange={handleCostingChange}
              />
            </div>
          ))
        );
      }
      if (costing.CPV) {
        inputs.push(
          <div key="CPV">
            <strong className="strongaddadmin">Cost Per View:</strong>
            <input
              className="inputaddadmin inputPN"
              type="text"
              id="CPV"
              name="CPV"
              placeholder="Enter Cost Per View"
              value={costing.CPV || ""}
              onChange={handleCostingChange}
            />
          </div>
        );
      }
    }
    
    // Add Patient Enrollment field before the AI Models section
    inputs.push(
      <div>
        <strong className="strongaddadmin">Patient Enrollment:</strong>
        <input
          className="inputaddadmin inputPN"
          type="text"
          name="PatientAddFee"
          placeholder="Enter Patient Enrollment Amount"
          value={costing.PatientAddFee || ""}
          onChange={handleCostingChange}
        />
      </div>
    );

    // Render the AI Models selection section for ALL costing types
    inputs.push(
      <div className="AI_Models_Enabled">
        <strong className="strongaddadmin">Select AI Models:</strong>

        {/* CXR-TB */}
        <div className="model-selection">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={aiEnabled["CXR-TB"] || false}
                onChange={(e) =>
                  updateAiEnabled("CXR-TB", e.target.checked)
                }
              />
              CXR-TB
            </label>
            {aiEnabled["CXR-TB"] && (
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={autoRunEnabled["CXR-TB"] || false}
                  onChange={(e) =>
                    updateAutoRunEnabled("CXR-TB", e.target.checked)
                  }
                />
                AUTO RUN
              </label>
            )}
          </div>
          {aiEnabled["CXR-TB"] && (
            <div className="input-group">
              <input
                className="inputaddadmin inputPN"
                type="text"
                name="CXR-TB"
                placeholder="Enter Price"
                value={costing["CXR-TB"] || ""}
                onChange={handleCostingChange}
              />
            </div>
          )}
        </div>

        {/* CXR-TB2 */}
        <div className="model-selection">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={aiEnabled["CXR-TB2"] || false}
                onChange={(e) =>
                  updateAiEnabled("CXR-TB2", e.target.checked)
                }
              />
              CXR-TB2
            </label>
            {aiEnabled["CXR-TB2"] && (
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={autoRunEnabled["CXR-TB2"] || false}
                  onChange={(e) =>
                    updateAutoRunEnabled("CXR-TB2", e.target.checked)
                  }
                />
                AUTO RUN
              </label>
            )}
          </div>
          {aiEnabled["CXR-TB2"] && (
            <div className="input-group">
              <input
                className="inputaddadmin inputPN"
                type="text"
                name="CXR-TB2"
                placeholder="Enter Price"
                value={costing["CXR-TB2"] || ""}
                onChange={handleCostingChange}
              />
            </div>
          )}
        </div>

        {/* CXR-Multiclass */}
        <div className="model-selection">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={aiEnabled["CXR-Multiclass"] || false}
                onChange={(e) =>
                  updateAiEnabled("CXR-Multiclass", e.target.checked)
                }
              />
              CXR-Multiclass
            </label>
            {aiEnabled["CXR-Multiclass"] && (
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={autoRunEnabled["CXR-Multiclass"] || false}
                  onChange={(e) =>
                    updateAutoRunEnabled("CXR-Multiclass", e.target.checked)
                  }
                />
                AUTO RUN
              </label>
            )}
          </div>
          {aiEnabled["CXR-Multiclass"] && (
            <div className="input-group">
              <input
                className="inputaddadmin inputPN"
                type="text"
                name="CXR-Multiclass"
                placeholder="Enter Price"
                value={costing["CXR-Multiclass"] || ""}
                onChange={handleCostingChange}
              />
            </div>
          )}
        </div>

        {/* XRay-Fracture */}
        <div className="model-selection">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={aiEnabled["XRay-Fracture"] || false}
                onChange={(e) =>
                  updateAiEnabled("XRay-Fracture", e.target.checked)
                }
              />
              XRay-Fracture
            </label>
            {aiEnabled["XRay-Fracture"] && (
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={autoRunEnabled["XRay-Fracture"] || false}
                  onChange={(e) =>
                    updateAutoRunEnabled("XRay-Fracture", e.target.checked)
                  }
                />
                AUTO RUN
              </label>
            )}
          </div>
          {aiEnabled["XRay-Fracture"] && (
            <div className="input-group">
              <input
                className="inputaddadmin inputPN"
                type="text"
                name="XRay-Fracture"
                placeholder="Enter Price"
                value={costing["XRay-Fracture"] || ""}
                onChange={handleCostingChange}
              />
            </div>
          )}
        </div>

        {/* 3D-Abdomen */}
        <div className="model-selection">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={aiEnabled["3D-Abdomen"] || false}
                onChange={(e) =>
                  updateAiEnabled("3D-Abdomen", e.target.checked)
                }
              />
              3D-Abdomen
            </label>
            {aiEnabled["3D-Abdomen"] && (
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={autoRunEnabled["3D-Abdomen"] || false}
                  onChange={(e) =>
                    updateAutoRunEnabled("3D-Abdomen", e.target.checked)
                  }
                />
                AUTO RUN
              </label>
            )}
          </div>
          {aiEnabled["3D-Abdomen"] && (
            <div className="input-group">
              <input
                className="inputaddadmin inputPN"
                type="text"
                name="3D-Abdomen"
                placeholder="Enter Price"
                value={costing["3D-Abdomen"] || ""}
                onChange={handleCostingChange}
              />
            </div>
          )}
        </div>

        {/* 3D-HaN */}
        <div className="model-selection">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={aiEnabled["3D-HaN"] || false}
                onChange={(e) =>
                  updateAiEnabled("3D-HaN", e.target.checked)
                }
              />
              3D-HaN
            </label>
            {aiEnabled["3D-HaN"] && (
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={autoRunEnabled["3D-HaN"] || false}
                  onChange={(e) =>
                    updateAutoRunEnabled("3D-HaN", e.target.checked)
                  }
                />
                AUTO RUN
              </label>
            )}
          </div>
          {aiEnabled["3D-HaN"] && (
            <div className="input-group">
              <input
                className="inputaddadmin inputPN"
                type="text"
                name="3D-HaN"
                placeholder="Enter Price"
                value={costing["3D-HaN"] || ""}
                onChange={handleCostingChange}
              />
            </div>
          )}
        </div>

        {/* Contour-Abdomen */}
        <div className="model-selection">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={aiEnabled["Contour-Abdomen"] || false}
                onChange={(e) =>
                  updateAiEnabled("Contour-Abdomen", e.target.checked)
                }
              />
              Contour-Abdomen
            </label>
            {aiEnabled["Contour-Abdomen"] && (
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={autoRunEnabled["Contour-Abdomen"] || false}
                  onChange={(e) =>
                    updateAutoRunEnabled("Contour-Abdomen", e.target.checked)
                  }
                />
                AUTO RUN
              </label>
            )}
          </div>
          {aiEnabled["Contour-Abdomen"] && (
            <div className="input-group">
              <input
                className="inputaddadmin inputPN"
                type="text"
                name="Contour-Abdomen"
                placeholder="Enter Price"
                value={costing["Contour-Abdomen"] || ""}
                onChange={handleCostingChange}
              />
            </div>
          )}
        </div>

        {/* Contour-HaN */}
        <div className="model-selection">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={aiEnabled["Contour-HaN"] || false}
                onChange={(e) =>
                  updateAiEnabled("Contour-HaN", e.target.checked)
                }
              />
              Contour-HaN
            </label>
            {aiEnabled["Contour-HaN"] && (
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={autoRunEnabled["Contour-HaN"] || false}
                  onChange={(e) =>
                    updateAutoRunEnabled("Contour-HaN", e.target.checked)
                  }
                />
                AUTO RUN
              </label>
            )}
          </div>
          {aiEnabled["Contour-HaN"] && (
            <div className="input-group">
              <input
                className="inputaddadmin inputPN"
                type="text"
                name="Contour-HaN"
                placeholder="Enter Price"
                value={costing["Contour-HaN"] || ""}
                onChange={handleCostingChange}
              />
            </div>
          )}
        </div>

        {/* Tumor-Abdomen */}
        <div className="model-selection">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={aiEnabled["Tumor-Abdomen"] || false}
                onChange={(e) =>
                  updateAiEnabled("Tumor-Abdomen", e.target.checked)
                }
              />
              Tumor-Abdomen
            </label>
            {aiEnabled["Tumor-Abdomen"] && (
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={autoRunEnabled["Tumor-Abdomen"] || false}
                  onChange={(e) =>
                    updateAutoRunEnabled("Tumor-Abdomen", e.target.checked)
                  }
                />
                AUTO RUN
              </label>
            )}
          </div>
          {aiEnabled["Tumor-Abdomen"] && (
            <div className="input-group">
              <input
                className="inputaddadmin inputPN"
                type="text"
                name="Tumor-Abdomen"
                placeholder="Enter Price"
                value={costing["Tumor-Abdomen"] || ""}
                onChange={handleCostingChange}
              />
            </div>
          )}
        </div>

        {/* Liver-Segmentation */}
        <div className="model-selection">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <label>
              <input
                type="checkbox"
                checked={aiEnabled["Liver-Segmentation"] || false}
                onChange={(e) =>
                  updateAiEnabled("Liver-Segmentation", e.target.checked)
                }
              />
              Liver-Segmentation
            </label>
            {aiEnabled["Liver-Segmentation"] && (
              <label style={{ marginLeft: '20px' }}>
                <input
                  type="checkbox"
                  checked={autoRunEnabled["Liver-Segmentation"] || false}
                  onChange={(e) =>
                    updateAutoRunEnabled("Liver-Segmentation", e.target.checked)
                  }
                />
                AUTO RUN
              </label>
            )}
          </div>
          {aiEnabled["Liver-Segmentation"] && (
            <div className="input-group">
              <input
                className="inputaddadmin inputPN"
                type="text"
                name="Liver-Segmentation"
                placeholder="Enter Price"
                value={costing["Liver-Segmentation"] || ""}
                onChange={handleCostingChange}
              />
            </div>
          )}
        </div>
        
        {/* Support for legacy model names */}
    
      </div>
    );
    
    // Return all inputs
    return inputs;
  };

  const notifySuccess = () =>
    toast.success("Admins Information Updated Successfully", {
      position: "top-center",
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!FullName || !Password || !Email) {
      toast.error("Please provide value in each field", {
        position: "top-center",
      });
    } else {
      try {
        // First, prepare the costing object by removing any unticked models
        const updatedCosting = { ...costing };
        
        // Clean up the updatedCosting object and aiEnabled object
        // 1. Remove unticked models from costing
        modelsToRemove.forEach(modelName => {
          if (updatedCosting[modelName]) {
            delete updatedCosting[modelName];
          }
        });
        
        // 2. Ensure each enabled model has a price and vice versa
        const finalEnabledModels = { ...aiEnabled };
        
        // For each model with a price, ensure it's marked as enabled
        Object.keys(updatedCosting).forEach(modelName => {
          const aiModelNames = [
            "CXR-TB", 
            "CXR-TB2",
            "CXR-Multiclass",
            "XRay-Fracture",
            "3D-Abdomen", 
            "3D-HaN",
            "Contour-Abdomen",
            "Contour-HaN",
            "Tumor-Abdomen",
            "Liver-Segmentation",
            "Bone_Fracture_Model",
            "ThreeD_AI_Model",
            "Brain_Tumour_Model",
            "ICH_Model",
            "Contouring_Model"
          ];
          
          // Only process valid AI model names, not other costing fields
          if (aiModelNames.includes(modelName)) {
            // If the model has a valid price but isn't enabled, enable it
            if (updatedCosting[modelName] && updatedCosting[modelName].toString().trim() !== "") {
              finalEnabledModels[modelName] = true;
            }
            // If the model is enabled but has no price, remove it from enabled
            else if (!updatedCosting[modelName] || updatedCosting[modelName].toString().trim() === "") {
              delete finalEnabledModels[modelName];
              delete updatedCosting[modelName]; // Also remove from costing if empty price
            }
          }
        });

        // Create a state object without the costing property
        const { costing: _, ...stateWithoutCosting } = state;
        
        // Update admin data without the costing object
        await set(ref(db, `superadmin/admins/${id}`), stateWithoutCosting);
        
        // Save/update currency
        await set(ref(db, `superadmin/admins/${id}/Currency`), currency);
        
        // Update the costing node directly with clean costing data
        await set(ref(db, `superadmin/admins/${id}/costing`), updatedCosting);
        
        // Update the AI_Model_Enabled node with the finalized enabled models
        await set(ref(db, `superadmin/admins/${id}/AI_Model_Enabled`), finalEnabledModels);
        
        // Update the AI_Model_AutoRun node with the auto run settings
        await set(ref(db, `superadmin/admins/${id}/AI_Model_AutoRun`), autoRunEnabled);
        
        // Reset the modelsToRemove array since changes are now saved
        setModelsToRemove([]);
        
        notifySuccess();
      } catch (error) {
        toast.error(error.message, {
          position: "top-center",
        });
      }
    }
  };

  return (
    <>
      <h2 className="text-center h2addadmin">Update Admin's Information</h2>
      <Link to="/SuperAdmin?section=viewAdmins" className="homeButton">
        <span id="backText" className="backText">
          Back
        </span>
        <i id="backIcon" className="fas fa-long-arrow-alt-left fa-lg fa-2x"></i>
      </Link>
      <div style={{ marginTop: "100px" }}>
        <form className="signupForm" onSubmit={handleSubmit}>
          <strong className="strongaddadmin">Admins Name:</strong>
          <input
            className="inputaddadmin"
            type="text"
            id="FullName"
            name="FullName"
            placeholder="Admin Name"
            value={FullName || ""}
            onChange={handleInputChange}
          />

          <strong className="strongaddadmin">Email:</strong>
          <input
            className="inputaddadmin inputPN"
            type="text"
            id="Email"
            name="Email"
            placeholder="Email ID"
            value={Email || ""}
            readOnly
            onChange={handleInputChange}
          />


          <strong className="strongaddadmin">Password:</strong>
          <input
            className="inputaddadmin inputPN"
            type="text"
            id="Password"
            name="Password"
            placeholder="Password"
            value={Password || ""}
            readOnly
            onChange={handleInputChange}
          />

          {/* Currency selection dropdown */}
          <strong className="strongaddadmin">Contact:</strong>
          <MuiTelInput
            value={contact}
            onChange={handleContactChange}
            defaultCountry="IN"
            id="contact"
            name="contact"
            required
            placeholder="Enter contact number"
            inputProps={{ className: "inputaddadmin inputPN" }}
            sx={{
              '& .MuiInputBase-root': {
               width: '130%',
              padding: '0',
              marginLeft: '-15%',
                border: '1px solid #ccc',
                borderRadius: '6px',
                background: 'white',
                boxSizing: 'border-box',
                fontSize: '16px',
                minHeight: 'unset',
              },
              '& input': {
                padding: '9px 10px',
                fontSize: '16px',
              }
            }}
          />
          <strong className="strongaddadmin">Currency:</strong>
          <select
            className="inputaddadmin"
            value={currency}
            onChange={e => setCurrency(e.target.value)}
            style={{ marginBottom: '16px' }}
            required
          >
            <option value="" disabled>Choose Currency</option>
            <option value="INR">INR (₹)</option>
            <option value="USD">USD ($)</option>
          </select>

          <strong className="strongaddadmin">Costing Type:</strong>
          <input
            className="inputaddadmin inputPN"
            type="text"
            id="CostingType"
            name="CostingType"
            placeholder="NA"
            value={CostingType || ""}
            readOnly
            onChange={handleInputChange}
          />

          <input
            type="hidden"
            id="Role"
            name="Role"
            placeholder="Role"
            value={Role || ""}
            onChange={handleInputChange}
          />

          {renderCostingInputs()}

          <input className="addadminbtn" type="submit" value="Save" />
          <ToastContainer />
        </form>
      </div>
    </>
  );
}

export default EditAdmin;
